import { useState } from 'react';
import { Link } from 'react-router-dom';
import { siteConfig, features, faqs, comparisonData, reviews } from '@/mocks/siteData';
import { OrganizationSchema, WebsiteSchema, WebPageSchema, FAQSchema } from '@/components/base/SchemaOrg';

export default function Home() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <OrganizationSchema />
      <WebsiteSchema />
      <WebPageSchema
        pageTitle={`${siteConfig.name} - ${siteConfig.tagline}`}
        pageDescription={siteConfig.description}
      />
      <FAQSchema faqs={faqs} />

      {/* Hero Section */}
      <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20gradient%20background%20with%20soft%20amber%20and%20cream%20tones%2C%20subtle%20geometric%20patterns%2C%20minimalist%20modern%20design%2C%20clean%20elegant%20aesthetic%2C%20flowing%20organic%20shapes%2C%20professional%20atmosphere%2C%20soft%20light%20diffusion%2C%20premium%20texture%2C%20no%20text%20no%20people&width=1800&height=900&seq=hero-doubao-main&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/35"></div>
        </div>
        <div className="relative z-10 w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-6">
              <div className="w-2 h-2 rounded-full bg-accent-400 animate-pulse"></div>
              <span className="text-sm text-background-50 font-medium">
                2026年全新升级，已服务超过100万成年用户
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 font-heading leading-tight mb-6">
              {siteConfig.tagline}
            </h1>
            <p className="text-base md:text-lg text-background-50/85 max-w-2xl mx-auto mb-8 leading-relaxed">
              {siteConfig.description}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/download"
                className="px-8 py-3.5 bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 text-sm whitespace-nowrap"
              >
                立即体验 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
              <Link
                to="/category/features"
                className="px-8 py-3.5 bg-background-50/15 backdrop-blur-sm text-background-50 font-semibold rounded-full border border-background-50/30 hover:bg-background-50/25 transition-all duration-200 text-sm whitespace-nowrap"
              >
                了解更多
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 mb-4">
              核心功能
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-4">
              六大核心能力，全面赋能成年用户
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
              每一个功能都经过精心打磨，只为给你最专业的AI助手体验
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group p-6 md:p-8 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center mb-5 group-hover:bg-primary-500 transition-colors duration-300">
                  <i className={`${feature.icon} text-xl text-primary-600 group-hover:text-background-50 transition-colors duration-300`}></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-3 font-heading">
                  {feature.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 mb-4">
              版本对比
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-4">
              成人版 vs 普通版，一目了然
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
              看看豆包成人版相比普通版到底强在哪里
            </p>
          </div>

          <div className="overflow-x-auto rounded-xl border border-background-200/70 bg-background-50">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-background-200/70">
                  <th className="text-left px-6 py-4 text-sm font-semibold text-foreground-900 font-heading">
                    功能维度
                  </th>
                  <th className="text-center px-6 py-4 text-sm font-semibold text-foreground-600">
                    普通版
                  </th>
                  <th className="text-center px-6 py-4 text-sm font-semibold bg-primary-50 text-primary-700 rounded-t-lg">
                    成人版
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.dimensions.map((dim, index) => (
                  <tr key={index} className="border-b border-background-100 last:border-0">
                    <td className="px-6 py-4 text-sm text-foreground-800 font-medium">
                      {dim}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {comparisonData.regular[index] ? (
                        <span className="inline-flex items-center gap-1 text-accent-600">
                          <i className="ri-check-line text-base"></i>
                          <span className="text-xs">支持</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-foreground-400">
                          <i className="ri-close-line text-base"></i>
                          <span className="text-xs">不支持</span>
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center bg-primary-50/50">
                      <span className="inline-flex items-center gap-1 text-primary-600">
                        <i className="ri-check-double-line text-base"></i>
                        <span className="text-xs font-medium">增强支持</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-center mt-8">
            <Link
              to="/category/comparison"
              className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              查看详细对比 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 mb-4">
              用户口碑
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-4">
              来自真实用户的声音
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
              超过百万成年用户选择了豆包成人版，听听他们怎么说
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <div
                key={index}
                className="p-6 rounded-xl bg-background-100 border border-background-200/70"
              >
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i
                      key={i}
                      className={`text-sm ${
                        i < review.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'
                      }`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  &ldquo;{review.content}&rdquo;
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-background-200/60">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary-600">
                      {review.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                    <p className="text-xs text-foreground-500">{review.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-background-100">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 mb-4">
              常见问题
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 font-heading mb-4">
              你关心的，都在这里
            </h2>
            <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
              关于豆包成人版最常见的问题和解答
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="rounded-xl border border-background-200/70 bg-background-50 overflow-hidden"
              >
                <button
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-background-50/80 transition-colors"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span className="text-sm font-medium text-foreground-900 pr-4">
                    {faq.question}
                  </span>
                  <i
                    className={`text-lg text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
                      openFaq === index ? 'ri-subtract-line rotate-180' : 'ri-add-line'
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-96 pb-4' : 'max-h-0'
                  }`}
                >
                  <p className="px-6 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 md:py-28 px-4 md:px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20abstract%20flowing%20gradient%20in%20amber%20and%20cream%20tones%2C%20soft%20organic%20shapes%2C%20minimalist%20modern%20design%2C%20elegant%20professional%20atmosphere%2C%20smooth%20textures%2C%20warm%20inviting%20feeling%2C%20clean%20composition%2C%20no%20text&width=1800&height=600&seq=cta-doubao&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-900/85 to-foreground-900/70"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-background-50 font-heading mb-4">
            准备好升级你的AI体验了吗？
          </h2>
          <p className="text-background-50/80 text-sm md:text-base mb-8 max-w-xl mx-auto">
            加入百万成年用户的行列，体验豆包成人版带来的专业AI智能服务
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/category/download"
              className="px-8 py-3.5 bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 text-sm whitespace-nowrap"
            >
              立即免费试用
            </Link>
            <Link
              to="/about"
              className="px-8 py-3.5 bg-background-50/10 backdrop-blur-sm text-background-50 font-semibold rounded-full border border-background-50/25 hover:bg-background-50/20 transition-all duration-200 text-sm whitespace-nowrap"
            >
              了解更多关于我们
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}